import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

// Register Chart.js components
Chart.register(...registerables);

interface AdminDashboardChartProps {
  title: string;
  data: any[];
  labels: string[];
  dataKey: string;
  type: 'line' | 'bar' | 'pie' | 'doughnut';
  color?: string;
  height?: number;
  loading?: boolean;
}

export default function AdminDashboardChart({ 
  title, 
  data,
  labels,
  dataKey,
  type = 'line',
  color = '#1D4E4E',
  height = 300,
  loading = false
}: AdminDashboardChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (loading || !chartRef.current) return;

    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Create gradient for background
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, `${color}20`); // 20% opacity
    gradient.addColorStop(1, `${color}05`); // 5% opacity

    // Create chart
    chartInstance.current = new Chart(ctx, {
      type,
      data: {
        labels,
        datasets: [
          {
            label: title,
            data: data.map(item => item[dataKey]),
            backgroundColor: type === 'line' ? gradient : color,
            borderColor: color,
            borderWidth: 2,
            tension: 0.4,
            fill: type === 'line',
            pointBackgroundColor: color,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            titleColor: '#1F2937',
            bodyColor: '#4B5563',
            borderColor: '#E5E7EB',
            borderWidth: 1,
            padding: 12,
            boxPadding: 6,
            usePointStyle: true,
            callbacks: {
              // Format tooltip values
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y;
                }
                return label;
              }
            }
          },
        },
        scales: type === 'line' || type === 'bar' ? {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#6B7280',
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: '#F3F4F6',
            },
            ticks: {
              color: '#6B7280',
              precision: 0,
            },
          },
        } : undefined,
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, labels, dataKey, type, color, height, loading, title]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-elevation-1">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {loading ? (
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      ) : (
        <div style={{ height: `${height}px` }}>
          <canvas ref={chartRef}></canvas>
        </div>
      )}
    </div>
  );
}